import { v4 as uuidv4 } from "uuid";

import {
  ILimitWindow,
  IModelCategory,
  LimitWindowType,
} from "features/usage-config/types/usage-enforcement.model";

import { keyFromName, uniqueKey } from "./usage-keys";

/** An hourly window divides the same working month a daily one does: 22 working days is
 * 22 × 24 hours, not a fixed 30 × 24. The window still runs around the clock. */
export const HOURS_PER_DAY = 24;

export const DEFAULT_WINDOW_HOURS = 5;
export const DEFAULT_WORKING_DAYS = 22;
export const DEFAULT_DAYS_PER_WINDOW = 1;
/** The longest a daily window may run. Mirrors MAX_WINDOW_DAYS on the server, which keeps
 * the window counters exactly this long; test_usage_windows holds the two together. */
export const MAX_WINDOW_DAYS = 14;
export const DEFAULT_BURST = 1.5;

const clampInt = (value: number, min: number, max: number, fallback: number): number => {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.round(value)));
};

export const clampHours = (value: number): number => clampInt(value, 1, 24, DEFAULT_WINDOW_HOURS);

export const clampWorkingDays = (value: number): number =>
  clampInt(value, 1, 31, DEFAULT_WORKING_DAYS);

export const clampDaysPerWindow = (value: number): number =>
  clampInt(value, 1, MAX_WINDOW_DAYS, DEFAULT_DAYS_PER_WINDOW);

export const clampBurst = (value: number): number => {
  if (!Number.isFinite(value)) return DEFAULT_BURST;
  const clamped = Math.min(10, Math.max(0.1, value));
  return Math.round(clamped * 100) / 100;
};

export const clampShare = (value: number): number => clampInt(value, 0, 100, 0);

/** A starting key from the name, deduplicated against every key already taken -- the other
 * windows' and the categories', since they share one namespace. */
export const windowKeyFromName = (name: string, taken: readonly string[]): string =>
  uniqueKey(keyFromName(name), taken);

export const createLimitWindow = (
  name: string,
  type: LimitWindowType,
  taken: readonly string[],
): ILimitWindow | null => {
  const key = windowKeyFromName(name, taken);
  if (!key) return null;
  return {
    key,
    revision: uuidv4(),
    name: name.trim(),
    type,
    enabled: true,
    hours: DEFAULT_WINDOW_HOURS,
    working_days: DEFAULT_WORKING_DAYS,
    days_per_window: DEFAULT_DAYS_PER_WINDOW,
    burst: DEFAULT_BURST,
  };
};

/** How many of this window fit in a month, before the span is applied. */
export const windowsInMonth = (limitWindow: ILimitWindow): number =>
  limitWindow.type === "hourly"
    ? (limitWindow.working_days * HOURS_PER_DAY) / limitWindow.hours
    : limitWindow.working_days;

/** Hourly windows are one slice. Daily windows span `days_per_window` working days. */
export const span = (limitWindow: ILimitWindow): number =>
  limitWindow.type === "hourly" ? 1 : limitWindow.days_per_window;

/** Unclamped fraction of a month. Can exceed 1 when a long span meets a high burst. */
export const rawMonthShare = (limitWindow: ILimitWindow): number =>
  (span(limitWindow) * limitWindow.burst) / windowsInMonth(limitWindow);

/** The fraction of a month this window may take, never more than all of it. */
export const monthShare = (limitWindow: ILimitWindow): number =>
  Math.min(1, rawMonthShare(limitWindow));

export const isClampedToMonth = (limitWindow: ILimitWindow): boolean =>
  rawMonthShare(limitWindow) > 1;

export const hoursLong = (limitWindow: ILimitWindow): number =>
  limitWindow.type === "hourly" ? limitWindow.hours : limitWindow.days_per_window * 24;

/**
 * Whether `limitWindow` can stop a request `shorter` would have allowed.
 * Same burst never binds: repeating the shorter window until it fills the longer
 * one lands on the same allowance, so the shorter one fires first.
 */
export const bindsAfter = (limitWindow: ILimitWindow, shorter: ILimitWindow): boolean => {
  const repeats = hoursLong(limitWindow) / hoursLong(shorter);
  // Equal shares must not bind. The two products are the same allowance, and float
  // dust would otherwise make the longer window look stricter.
  return repeats > 1 && monthShare(limitWindow) + 1e-9 < repeats * monthShare(shorter);
};

export const MIN_BURST = 0.1;

/**
 * The burst this window would have to drop below before it could ever be the one that
 * refuses. Windows told about the same working days share one scale, so this is the
 * shorter window's burst. Different working days move it.
 */
export const bindingBurst = (limitWindow: ILimitWindow, shorter: ILimitWindow): number => {
  const repeats = hoursLong(limitWindow) / hoursLong(shorter);
  return (repeats * monthShare(shorter) * windowsInMonth(limitWindow)) / span(limitWindow);
};

/**
 * The shortest enabled window that keeps this one from ever firing.
 * Null when this window can still bind, or when nothing shorter is on.
 */
export const blockedByShorter = (
  limitWindow: ILimitWindow,
  windows: readonly ILimitWindow[],
): ILimitWindow | null => {
  if (!limitWindow.enabled) return null;
  const shorter = windows.filter(
    (other) =>
      other.enabled && other.key !== limitWindow.key && hoursLong(other) < hoursLong(limitWindow),
  );
  if (shorter.length === 0) return null;
  if (shorter.some((other) => bindsAfter(limitWindow, other))) return null;
  return shorter.reduce((best, other) => (hoursLong(other) < hoursLong(best) ? other : best));
};

/**
 * This category's percentage of one window, or null when it set none.
 * Null is no limit. Zero is a real ceiling and must not be used as the stand-in.
 */
export const shareOf = (share: Record<string, number>, windowKey: string): number | null =>
  Object.prototype.hasOwnProperty.call(share, windowKey) ? share[windowKey] : null;

export const withWindowShare = (
  category: IModelCategory,
  windowKey: string,
  share: number | null,
): IModelCategory => {
  const windowShare = { ...category.window_share };
  if (share === null) delete windowShare[windowKey];
  else windowShare[windowKey] = clampShare(share);
  return { ...category, window_share: windowShare };
};

/** Move one window's shares onto its new key. Renaming the key must not drop the limits. */
export const renameWindowShare = (
  categories: IModelCategory[],
  from: string,
  to: string,
): IModelCategory[] =>
  from === to
    ? categories
    : categories.map((category) => {
        const share = shareOf(category.window_share, from);
        if (share === null) return category;
        const windowShare = { ...category.window_share };
        delete windowShare[from];
        windowShare[to] = share;
        return { ...category, window_share: windowShare };
      });

/** Drop one window's key from every category. Deleting the window has to drop its shares. */
export const withoutWindowShare = (
  categories: IModelCategory[],
  windowKey: string,
): IModelCategory[] =>
  categories.map((category) =>
    shareOf(category.window_share, windowKey) === null
      ? category
      : withWindowShare(category, windowKey, null),
  );

export const formatQuantity = (value: number): string =>
  value.toLocaleString("en-US", { maximumFractionDigits: 2 });

export const formatPercent = (fraction: number): string => `${(fraction * 100).toFixed(2)}%`;
